import { useEffect, useCallback, useRef, useState } from 'react';
import type {
  Settings,
  EventStatus,
} from '../pages/events/new-residents-orientation';
import logger from '../server/libs/logger';

// liveStream 設定値をポーリングで取得する間隔（分）
const pollingIntervalMinutes: number = 5;

// イベントステータスを更新する間隔（秒）
const updateStatusIntervalSec: number = 10;

const pollingApiPath =
  '/api/events/new-residents-orientation/live-stream-settings';

type UseEventStatusProps = {
  isLoggedIn: boolean;
  startDateTime: string;
  endDateTime: string;
  initialLiveStreamSettings: Settings['liveStream'];
};

// イベント開始予定日時までの残り時間
type DiffToEventStart = {
  days: number;
  hours: number;
  minutes: number;
};

type UseEventStatusReturn = {
  liveStreamSettings: Settings['liveStream'];
  eventStatus: EventStatus;
  isLiveStreamOnAir: boolean;
  diff: DiffToEventStart;
};

// イベント当日かどうかを判定する
const isEventDay = (startDateTime: string): boolean => {
  const start = new Date(startDateTime);
  if (isNaN(start.getTime())) {
    logger.error(`開始日時の値が不正です; startDateTime=${startDateTime}`);
    return false;
  }

  const now = new Date();
  return (
    start.getFullYear() === now.getFullYear() &&
    start.getMonth() === now.getMonth() &&
    start.getDate() === now.getDate()
  );
};

/**
 * 予定時刻からイベントが開始前、開催中、終了後のどれに該当するかを返す
 */
const getEventStatus = ({
  startDateTime,
  endDateTime,
}: {
  startDateTime: string;
  endDateTime: string;
}): EventStatus | null => {
  const start = new Date(startDateTime);
  const end = new Date(endDateTime);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    logger.error(
      `開始日時、終了日時の値が不正です; startDateTime=${startDateTime}, endDateTime=${endDateTime}`,
    );
    return null;
  }

  const now = new Date();
  let eventStatus: EventStatus;
  if (now < start) {
    eventStatus = 'before';
  } else if (now >= start && now < end) {
    eventStatus = 'ongoing';
  } else {
    eventStatus = 'ended';
  }
  return eventStatus;
};

/**
 * イベント開始までのdiffを計算する
 */
const getDiffToEventStart = ({
  startDateTime,
}: {
  startDateTime: string;
}): { days: number; hours: number; minutes: number } | null => {
  const start = new Date(startDateTime);
  if (isNaN(start.getTime())) {
    logger.error(`開始日時の値が不正です; startDateTime=${startDateTime}`);
    return null;
  }

  const now = new Date();
  const diff = start.getTime() - now.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  return { days, hours, minutes };
};

const getIsLiveStreamOnAir = ({
  streamStartAt,
  streamEndAt,
  youtubeVideoIdExists,
}: {
  streamStartAt: string;
  streamEndAt: string;
  youtubeVideoIdExists: boolean;
}): boolean => {
  if (!youtubeVideoIdExists) return false;

  const now = new Date();
  const streamStart = new Date(streamStartAt);
  const streamEnd = new Date(streamEndAt);
  if (isNaN(streamStart.getTime()) || isNaN(streamEnd.getTime())) {
    logger.error(
      `ライブストリーム開始日時、ライブストリーム終了日時の値が不正です; streamStartAt=${streamStartAt}, streamEndAt=${streamEndAt}`,
    );
    return false;
  }
  return now >= streamStart && now <= streamEnd;
};

/**
 *  2026年度 新・研修医向け 1dayオリエンテーション配信イベントのステータスをリアルタイムで返すhook
 */
export const useNewResidentsOrientationEventStatus = ({
  isLoggedIn,
  startDateTime,
  endDateTime,
  initialLiveStreamSettings,
}: UseEventStatusProps): UseEventStatusReturn => {
  const [eventStatus, setEventStatus] = useState<EventStatus>(
    getEventStatus({ startDateTime, endDateTime }) ?? 'before',
  );
  const isLiveStreamOnAirInitValue = getIsLiveStreamOnAir({
    streamStartAt: initialLiveStreamSettings.streamStartAt,
    streamEndAt: initialLiveStreamSettings.streamEndAt,
    youtubeVideoIdExists: !!initialLiveStreamSettings.youtubeVideoId,
  });
  const [isLiveStreamOnAir, setIsLiveStreamOnAir] = useState<boolean>(
    isLiveStreamOnAirInitValue,
  );
  const [diff, setDiff] = useState<DiffToEventStart>(
    getDiffToEventStart({ startDateTime }) ?? {
      days: 0,
      hours: 0,
      minutes: 0,
    },
  );
  const [liveStreamSettings, setLiveStreamSettings] = useState<
    Settings['liveStream']
  >(initialLiveStreamSettings);

  const pollingTimerRef = useRef<number | null>(null);
  const pollingStartTimeoutRef = useRef<number | null>(null);

  const updateEventStatus = useCallback(() => {
    const diffToEventStart = getDiffToEventStart({ startDateTime });
    if (diffToEventStart) {
      setDiff(diffToEventStart);
    }

    // manualStreamStatusの値があれば日時に関わらずステータスを上書きする
    if (liveStreamSettings.manualStreamStatus === 'start') {
      setEventStatus('ongoing');
      setIsLiveStreamOnAir(true);
      return;
    }
    if (liveStreamSettings.manualStreamStatus === 'stop') {
      setEventStatus('ended');
      setIsLiveStreamOnAir(false);
      return;
    }

    const eventStatus = getEventStatus({ startDateTime, endDateTime });
    if (eventStatus) {
      setEventStatus(eventStatus);
    }
    const isLiveStreamOnAir = getIsLiveStreamOnAir({
      streamStartAt: liveStreamSettings.streamStartAt,
      streamEndAt: liveStreamSettings.streamEndAt,
      youtubeVideoIdExists: !!liveStreamSettings.youtubeVideoId,
    });
    setIsLiveStreamOnAir(isLiveStreamOnAir);
  }, [
    startDateTime,
    endDateTime,
    liveStreamSettings.manualStreamStatus,
    liveStreamSettings.streamStartAt,
    liveStreamSettings.streamEndAt,
    liveStreamSettings.youtubeVideoId,
  ]);

  const fetchLiveStreamSettings = useCallback(async () => {
    try {
      const response = await fetch(pollingApiPath);
      if (response.ok) {
        const data: Settings['liveStream'] = await response.json();
        setLiveStreamSettings(data);
      } else {
        throw new Error();
      }
    } catch (e) {
      console.error('Failed to fetch api:', e);
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollingTimerRef.current || !isLoggedIn) return;

    // 即座に1回呼び出す
    fetchLiveStreamSettings();

    // 5分ごとにポーリング開始
    pollingTimerRef.current = window.setInterval(
      fetchLiveStreamSettings,
      pollingIntervalMinutes * 60 * 1000,
    );
  }, [fetchLiveStreamSettings, isLoggedIn]);

  /**
   * ステータス更新
   */
  useEffect(() => {
    updateEventStatus();

    // イベント当日でなけれはばステータス更新のタイマーもポーリングも開始しない
    if (!isEventDay(startDateTime)) {
      return;
    }
    const statusTimer = setInterval(
      updateEventStatus,
      updateStatusIntervalSec * 1000,
    );

    return () => clearInterval(statusTimer);
  }, [updateEventStatus, startDateTime]);

  /**
   * streamStartAt に達したらポーリング開始
   */
  useEffect(() => {
    if (!liveStreamSettings?.streamStartAt) return;

    const streamStartTime = new Date(
      liveStreamSettings.streamStartAt,
    ).getTime();
    if (Number.isNaN(streamStartTime)) {
      logger.error(
        `ライブストリーム開始日時の値が不正です; streamStartAt=${liveStreamSettings.streamStartAt}`,
      );
      return;
    }

    const now = Date.now();
    if (now >= streamStartTime) {
      startPolling();
    } else {
      // streamStartTimeより前に画面を開いた場合、初回のポーリングを開始するためのタイマーをセット
      pollingStartTimeoutRef.current = window.setTimeout(() => {
        startPolling();
        pollingStartTimeoutRef.current = null;
      }, streamStartTime - now);
    }

    return () => {
      if (pollingStartTimeoutRef.current) {
        clearTimeout(pollingStartTimeoutRef.current);
        pollingStartTimeoutRef.current = null;
      }
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, [liveStreamSettings?.streamStartAt, startPolling]);

  return {
    eventStatus,
    isLiveStreamOnAir,
    diff,
    liveStreamSettings,
  };
};
